/** The PostHog reverse proxy. A CNAME to …cf-prod-us-proxy.proxyhog.com that
 *  PostHog operates: there is no Worker and no route behind this hostname, and
 *  this repo must not build one.
 *
 *  ⚠️ The posthog-js loader derives its assets host by string-replacing
 *  `.i.posthog.com` inside `api_host`. On a proxy domain that replace is a
 *  no-op, so `array.js` is fetched from here too. One origin, not two. */
export const POSTHOG_API_HOST = 'https://p.ayso13.org'

/** Where links GENERATED in the client (toolbar, session links) should point.
 *  Not a network origin — the proxy does not serve the PostHog UI. */
export const POSTHOG_UI_HOST = 'https://us.posthog.com'

/** ⚠️ EVERY SCRAPING FEATURE IS SPELLED OUT AS OFF, NOT LEFT UNSET. posthog-js
 *  treats an unset `capture_dead_clicks` or `capture_exceptions` as "follow
 *  the project's remote settings", so one toggle in the PostHog UI would start
 *  sending dead-click events (they carry the clicked element's text) and
 *  exception events (messages and stacks) from pages that render children's
 *  names. Masking text and attributes is the second layer if anything that
 *  scrapes the DOM is ever switched on anyway. Surveys are off because nothing
 *  here should put a PostHog-authored form in front of a parent.
 *
 *  ⚠️ FLAGS ARE OFF BECAUSE `/flags` IS NOT AN EVENT. Nothing here reads a flag, and
 *  every `identify` would otherwise POST `$initial_current_url` to `/flags`, out of a
 *  cookie shared by every subdomain and past `before_send`. Reading a flag means
 *  making that address safe first.
 *
 *  ⚠️ `t` AND `h` ARE MASKED BECAUSE `before_send` SEES ONLY EVENTS. A `/submit` link
 *  keeps them in the address, and posthog-js writes the address into `$initial_person_info`,
 *  a year in a `.ayso13.org` cookie, and `$client_session_props` in local storage. With
 *  the mask on, every URL it reads has those two values replaced by `<masked>`, as well
 *  as its own list of ad click ids. UTM tags are not on that list. It does NOT reach
 *  `document.referrer`, so `r` beside `u` is whatever the page was reached from.
 *
 *  Heatmaps are NOT in this list: they record positions, not text, and are on
 *  for some surfaces on purpose (`SURFACE_CONFIG`). With `rageclick: false`
 *  they simply stop marking rage-click points.
 *
 *  ONE OBJECT FOR BOTH INIT SITES. `initAnalytics` spreads it and
 *  `posthogSnippet` writes it out key by key, so neither can drop a key the
 *  other keeps. */
export const POSTHOG_PRIVACY_CONFIG = {
  capture_dead_clicks: false,
  capture_exceptions: false,
  rageclick: false,
  mask_all_text: true,
  mask_all_element_attributes: true,
  disable_surveys: true,
  advanced_disable_flags: true,
  mask_personal_data_properties: true,
  custom_personal_data_properties: ['t', 'h'] as string[],
} as const

/** Events `before_send` drops in the browser, whatever the config above or the
 *  project settings say. The config is what stops them being generated; this
 *  is the backstop if a posthog-js release adds a way to turn one back on that
 *  the config does not cover. Every one of them can carry page text. */
export const BLOCKED_EVENTS: readonly string[] = [
  '$autocapture',
  '$rageclick',
  '$dead_click',
  '$dead_swipe',
  '$exception',
]

/** A URL, absolute or relative, and its query string, as a RegExp source with the
 *  part to keep in group 1. Both `before_send` hooks build their pattern from this.
 *  Everything from the `?` on goes: a fragment after a query goes with it.
 *
 *  ⚠️ `<masked>` IS NAMED BECAUSE A QUERY OTHERWISE ENDS AT ITS `<`. posthog-js masks
 *  before this runs (`mask_personal_data_properties`), and without it `?t=<masked>&a=1`
 *  would leave `<masked>&a=1` stuck to the path. */
export const URL_QUERY_PATTERN = `((?:https?:\\/\\/|\\/)[^\\s?"'<>]*)\\?(?:<masked>|[^\\s"'<>])+`

/** How far `before_send` walks into an event. No event nests near this (a web
 *  vitals metric is three levels), so anything deeper is dropped, not sent unread. */
export const URL_SCRUB_MAX_DEPTH = 12

export interface SurfaceConfig {
  /** Heatmaps need no autocapture and record positions rather than text, so
   *  they are safe where no personal data renders. Off on the three surfaces
   *  that put children's names in the DOM. */
  heatmaps: boolean
}

/** ⚠️ THE SURFACE LIST IS THIS OBJECT, and the `Surface` type is derived from
 *  it rather than declared alongside it. A hand-maintained union next to a
 *  hand-maintained config is two lists that drift; one is one. */
export const SURFACE_CONFIG = {
  // Operator tool behind Cloudflare Access. NOT the same surface as `public`,
  // even though both are served from schedule.ayso13.org: merging anonymous
  // visitor traffic with operator sessions makes every adoption number
  // meaningless.
  admin: { heatmaps: false },
  coach: { heatmaps: false },
  stats: { heatmaps: false },
  /** The anonymous card-submission wizard at stats.ayso13.org/submit. NOT
   *  `stats`, for the reason the `admin`/`public` pair above already states:
   *  that rule is about AUDIENCES, not products, and these are a stranger with
   *  a paper card and the region's one statistician, on one origin and one
   *  token. Hundreds of anonymous referees a weekend would swamp "is the
   *  review queue being used", which is the first question the instrumentation
   *  was added to answer.
   *
   *  ⚠️ `card_submitted` is captured HERE too (apps/stats/src/index.ts), not on
   *  `stats`: it is the submitter's own completion, and filing the open and the
   *  submission under different surfaces is the split this exists to avoid. */
  submit: { heatmaps: false },
  /** The anonymous report wizards at stats.ayso13.org/report. NOT `stats`, for
   *  the audience reason above: strangers filing reports, not the statistician.
   *  No heatmaps: the page can show jersey numbers and, on a signed link,
   *  children's names. */
  report: { heatmaps: false },
  referee: { heatmaps: true },
  console: { heatmaps: true },
  fields: { heatmaps: true },
  /** The unauthenticated SSR schedule site. Nobody is ever identified here. */
  public: { heatmaps: true },
  /** Background Workers — cron triggers and the queue consumer. NOT `admin`:
   *  nobody is signed in and nobody opened anything, so filing a 3am cron
   *  failure on the operator surface pollutes exactly the adoption number the
   *  spec forbids merging. Server-side only, so `heatmaps` is inert here — the
   *  key exists because every surface carries the same config shape. */
  worker: { heatmaps: false },
} as const satisfies Record<string, SurfaceConfig>

export type Surface = keyof typeof SURFACE_CONFIG

export const ALL_SURFACES = Object.keys(SURFACE_CONFIG) as Surface[]
